import React, { useState } from "react";
import {
  X,
  Save,
  Calendar,
  Heart,
  Activity as ActivityIcon,
  Award,
  Plus,
  Trash2,
} from "lucide-react";
import apiClient from "@/utils/apiClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { examTypesCollection } from "@/db/collection";

const StudentUpdateModal = ({ isOpen, onClose, student, onSuccess }) => {
  const [activeTab, setActiveTab] = useState("attendance");
  const [loading, setLoading] = useState(false);
  const [recordsAddedCount, setRecordsAddedCount] = useState({
    attendance: 0,
    health: 0,
    activities: 0,
    results: 0,
  });
  const examTypesData = examTypesCollection();

  // Attendance with date range
  const [attendanceData, setAttendanceData] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    status: "present",
    notes: "",
  });

  // Health with custom "other" option
  const [healthData, setHealthData] = useState({
    date: new Date().toISOString().split("T")[0],
    status: "healthy",
    customStatus: "",
    notes: "",
    visit_details: "",
  });

  // Activities with date range
  const [activityData, setActivityData] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
    activity_type: "sports",
    title: "",
    description: "",
  });

  // Results with tabular format
  const [resultData, setResultData] = useState({
    exam_type: "unit_test",
    custom_exam_type: "",
    subjects: [{ subject: "", marks_obtained: "", total_marks: "" }],
  });

  const handleAttendanceSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Generate dates between startDate and endDate
      const start = new Date(attendanceData.startDate);
      const end = new Date(attendanceData.endDate);
      const dates = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toISOString().split("T")[0]);
      }

      // Submit bulk attendance
      await apiClient.post("/v1/admin/student_records/attendance/bulk", {
        student_id: student.id,
        dates: dates,
        status: attendanceData.status,
        notes: attendanceData.notes,
        hostel_id: student.hostel_id,
      });

      // Increment counter
      setRecordsAddedCount((prev) => ({
        ...prev,
        attendance: prev.attendance + dates.length,
      }));

      toast.success(`✅ Attendance recorded for ${dates.length} day(s)!`);

      // Reset form but keep modal open
      setAttendanceData({
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date().toISOString().split("T")[0],
        status: "present",
        notes: "",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to record attendance"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleHealthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const finalStatus =
        healthData.status === "other"
          ? healthData.customStatus
          : healthData.status;

      await apiClient.post("/v1/admin/student_records/health", {
        student_id: student.id,
        date: healthData.date,
        status: finalStatus,
        notes: healthData.notes,
        visit_details: healthData.visit_details || null,
        hostel_id: student.hostel_id,
      });

      // Increment counter
      setRecordsAddedCount((prev) => ({
        ...prev,
        health: prev.health + 1,
      }));

      toast.success("✅ Health record added successfully!");

      // Reset form but keep modal open
      setHealthData({
        date: new Date().toISOString().split("T")[0],
        status: "healthy",
        customStatus: "",
        notes: "",
        visit_details: "",
      });
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to add health record"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleActivitySubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Generate dates between startDate and endDate
      const start = new Date(activityData.startDate);
      const end = new Date(activityData.endDate);
      const dates = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(new Date(d).toISOString().split("T")[0]);
      }

      // Submit bulk activities
      await apiClient.post("/v1/admin/student_records/activities/bulk", {
        student_id: student.id,
        dates: dates,
        activity_type: activityData.activity_type,
        title: activityData.title,
        description: activityData.description,
        hostel_id: student.hostel_id,
      });

      // Increment counter
      setRecordsAddedCount((prev) => ({
        ...prev,
        activities: prev.activities + dates.length,
      }));

      toast.success(`✅ Activity recorded for ${dates.length} day(s)!`);

      // Reset form but keep modal open
      setActivityData({
        startDate: new Date().toISOString().split("T")[0],
        endDate: new Date().toISOString().split("T")[0],
        activity_type: "sports",
        title: "",
        description: "",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record activity");
    } finally {
      setLoading(false);
    }
  };

  const handleResultSubmit = async (e) => {
    e.preventDefault();

    // 1. Basic Validation
    if (
      resultData.exam_type === "custom" &&
      !resultData.custom_exam_type.trim()
    ) {
      toast.error("Please enter custom exam type name");
      return;
    }

    // 2. Marks Validation Loop
    for (const sub of resultData.subjects) {
      const marks = parseFloat(sub.marks_obtained) || 0;
      const total = parseFloat(sub.total_marks) || 0;
      const subjectName = sub.subject.trim() || "Unnamed Subject";

      if (total <= 0) {
        toast.error(`Total marks for ${subjectName} must be greater than 0`);
        return;
      }


      if (marks > total) {
        toast.error(
          `In ${subjectName}: Obtained marks (${marks}) cannot exceed total marks (${total})`
        );
        return;
      }


    }

    setLoading(true);
    try {
      // 3. Prepare Data (since we already validated, we can map safely)
      const resultsWithCalculation = resultData.subjects.map((sub) => {
        const marks = parseFloat(sub.marks_obtained);
        const total = parseFloat(sub.total_marks);
        const percentage = ((marks / total) * 100).toFixed(2);

        return {
          subject: sub.subject,
          marks_obtained: marks,
          total_marks: total,
          percentage: parseFloat(percentage),
        };
      });

      const finalExamType =
        resultData.exam_type === "custom"
          ? resultData.custom_exam_type.trim()
          : resultData.exam_type;

      const response = await apiClient.post(
        "/v1/admin/student_records/results/bulk",
        {
          student_id: student.id,
          exam_type: finalExamType,
          exam_date: resultData.exam_date,
          results: resultsWithCalculation,
          hostel_id: student.hostel_id,
        }
      );

      setRecordsAddedCount((prev) => ({
        ...prev,
        results: prev.results + resultsWithCalculation.length,
      }));

      toast.success(
        response?.data?.message ||
          `✅ Results added for ${resultsWithCalculation.length} subject(s)!`
      );

      // Reset form
      setResultData({
        exam_type: "unit_test",
        exam_date: new Date().toISOString().split("T")[0],
        custom_exam_type: "",
        subjects: [{ subject: "", marks_obtained: "", total_marks: "" }],
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add results");
    } finally {
      setLoading(false);
    }
  };

  const addSubjectRow = () => {
    setResultData((prev) => ({
      ...prev,
      subjects: [
        ...prev.subjects,
        { subject: "", marks_obtained: "", total_marks: "" },
      ],
    }));
  };

  const removeSubjectRow = (index) => {
    setResultData((prev) => ({
      ...prev,
      subjects: prev.subjects.filter((_, i) => i !== index),
    }));
  };

  const updateSubject = (index, field, value) => {
    setResultData((prev) => ({
      ...prev,
      subjects: prev.subjects.map((sub, i) =>
        i === index ? { ...sub, [field]: value } : sub
      ),
    }));
  };

  const calculateTotalMarks = () => {
    const total = resultData.subjects.reduce((sum, sub) => {
      return sum + (parseFloat(sub.marks_obtained) || 0);
    }, 0);
    return total.toFixed(2);
  };

  const calculateTotalMaxMarks = () => {
    const total = resultData.subjects.reduce((sum, sub) => {
      return sum + (parseFloat(sub.total_marks) || 0);
    }, 0);
    return total.toFixed(2);
  };

  const calculateOverallPercentage = () => {
    const obtained = parseFloat(calculateTotalMarks());
    const max = parseFloat(calculateTotalMaxMarks());
    if (max === 0) return "0.00";
    return ((obtained / max) * 100).toFixed(2);
  };

  if (!isOpen) return null;

  const tabs = [
    { id: "attendance", label: "Attendance", icon: Calendar },
    { id: "health", label: "Health", icon: Heart },
    { id: "activities", label: "Activities", icon: ActivityIcon },
    { id: "results", label: "Results", icon: Award },
  ];
  const getTodayDate = () => new Date().toISOString().split("T")[0];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold" style={{ color: "#2C5F7F" }}>
              Update Student Records
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {student.name} - {student.class_name}
            </p>
            {/* Session counter */}
            {(recordsAddedCount.attendance > 0 ||
              recordsAddedCount.health > 0 ||
              recordsAddedCount.activities > 0 ||
              recordsAddedCount.results > 0) && (
              <div className="mt-2 flex gap-2 text-xs">
                {recordsAddedCount.attendance > 0 && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                    ✓ {recordsAddedCount.attendance} Attendance
                  </span>
                )}
                {recordsAddedCount.health > 0 && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                    ✓ {recordsAddedCount.health} Health
                  </span>
                )}
                {recordsAddedCount.activities > 0 && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                    ✓ {recordsAddedCount.activities} Activities
                  </span>
                )}
                {recordsAddedCount.results > 0 && (
                  <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">
                    ✓ {recordsAddedCount.results} Results
                  </span>
                )}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-b-2 text-blue-600"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                  style={activeTab === tab.id ? { borderColor: "#5A9FD4" } : {}}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Attendance Tab - WITH DATE RANGE */}
          {activeTab === "attendance" && (
            <form onSubmit={handleAttendanceSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={attendanceData.startDate}
                    onChange={(e) =>
                      setAttendanceData((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={attendanceData.endDate}
                    onChange={(e) =>
                      setAttendanceData((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    min={attendanceData.startDate}
                    // max should today
                    max={getTodayDate()}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                📅 Will mark attendance for all dates from{" "}
                {attendanceData.startDate} to {attendanceData.endDate}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={attendanceData.status}
                  onChange={(e) =>
                    setAttendanceData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="on_leave">On Leave</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={attendanceData.notes}
                  onChange={(e) =>
                    setAttendanceData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional notes..."
                />
              </div>
              <div className="space-y-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-white"
                >
                  {loading ? "Saving..." : "Save Attendance"}
                </Button>
                {recordsAddedCount.attendance > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = `/admin/students/${student.id}`)
                    }
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close & View Profile →
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Health Tab - WITH CUSTOM "OTHER" OPTION */}
          {activeTab === "health" && (
            <form onSubmit={handleHealthSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  value={healthData.date}
                  onChange={(e) =>
                    setHealthData((prev) => ({ ...prev, date: e.target.value }))
                  }
                  max={getTodayDate()}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Health Status
                </label>
                <select
                  value={healthData.status}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="healthy">Healthy</option>
                  <option value="minor_issue">Minor Issue</option>
                  <option value="sick">Sick</option>
                  <option value="hospital_visit">Hospital Visit</option>
                  <option value="other">Other (Specify)</option>
                </select>
              </div>

              {/* Custom status input when "Other" is selected */}
              {healthData.status === "other" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Specify Status
                  </label>
                  <input
                    type="text"
                    value={healthData.customStatus}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        customStatus: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter custom health status"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  value={healthData.notes}
                  onChange={(e) =>
                    setHealthData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Health notes, symptoms, medications, etc."
                />
              </div>

              {healthData.status === "hospital_visit" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Visit Details
                  </label>
                  <textarea
                    value={healthData.visit_details}
                    onChange={(e) =>
                      setHealthData((prev) => ({
                        ...prev,
                        visit_details: e.target.value,
                      }))
                    }
                    rows={2}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Hospital name, doctor, diagnosis..."
                  />
                </div>
              )}

              <div className="space-y-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-white"
                >
                  {loading ? "Saving..." : "Save Health Record"}
                </Button>
                {recordsAddedCount.health > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = `/admin/students/${student.id}`)
                    }
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close & View Profile →
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Activities Tab - WITH DATE RANGE */}
          {activeTab === "activities" && (
            <form onSubmit={handleActivitySubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={activityData.startDate}
                    onChange={(e) =>
                      setActivityData((prev) => ({
                        ...prev,
                        startDate: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={activityData.endDate}
                    onChange={(e) =>
                      setActivityData((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    min={activityData.startDate}
                    max={getTodayDate()}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded">
                📅 Will log activity for all dates from {activityData.startDate}{" "}
                to {activityData.endDate}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Activity Type
                </label>
                <select
                  value={activityData.activity_type}
                  onChange={(e) =>
                    setActivityData((prev) => ({
                      ...prev,
                      activity_type: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="sports">Sports</option>
                  <option value="arts">Arts & Crafts</option>
                  <option value="academic">Academic</option>
                  <option value="cultural">Cultural</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Activity Title
                </label>
                <input
                  type="text"
                  value={activityData.title}
                  onChange={(e) =>
                    setActivityData((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Football Practice, Art Class"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  value={activityData.description}
                  onChange={(e) =>
                    setActivityData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Activity details, performance notes..."
                />
              </div>
              <div className="space-y-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-white"
                >
                  {loading ? "Saving..." : "Save Activities"}
                </Button>
                {recordsAddedCount.activities > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = `/admin/students/${student.id}`)
                    }
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close & View Profile →
                  </button>
                )}
              </div>
            </form>
          )}

          {/* Results Tab - TABULAR FORMAT WITH AUTO-CALCULATION */}
          {activeTab === "results" && (
            <form onSubmit={handleResultSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Exam Type
                </label>
                <select
                  value={resultData.exam_type}
                  onChange={(e) =>
                    setResultData((prev) => ({
                      ...prev,
                      exam_type: e.target.value
                        ?.replaceAll(" ", "-")
                        ?.toLocaleLowerCase(),
                      custom_exam_type: "",
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {examTypesData?.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.name}
                    </option>
                  ))}
                </select>
              </div>
              {/* exam date */}
              <div>
                <label className="block text-sm font-medium mb-1">Date</label>
                <input
                  type="date"
                  value={resultData.exam_date}
                  onChange={(e) =>
                    setResultData((prev) => ({
                      ...prev,
                      exam_date: e.target.value,
                    }))
                  }
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Custom Exam Type Input */}
              {resultData.exam_type === "custom" && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Custom Exam Name *
                  </label>
                  <input
                    type="text"
                    value={resultData.custom_exam_type}
                    onChange={(e) =>
                      setResultData((prev) => ({
                        ...prev,
                        custom_exam_type: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Monthly Test, Surprise Quiz"
                    required
                  />
                </div>
              )}

              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Subject
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Marks Obtained
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                        Total Marks
                      </th>
                      <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">
                        %
                      </th>
                      <th className="px-4 py-2 text-center text-sm font-medium text-gray-700">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultData.subjects.map((subject, index) => {
                      const marks = parseFloat(subject.marks_obtained) || 0;
                      const total = parseFloat(subject.total_marks) || 0;
                      const percentage =
                        total > 0 ? ((marks / total) * 100).toFixed(2) : "0.00";

                      return (
                        <tr key={index} className="border-t">
                          <td className="px-4 py-2">
                            <input
                              type="text"
                              value={subject.subject}
                              onChange={(e) =>
                                updateSubject(index, "subject", e.target.value)
                              }
                              className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Subject name"
                              required
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={subject.marks_obtained}
                              onChange={(e) =>
                                // allow backspace and clear
                                e.key === "Backspace" || e.key === "Delete"
                                  ? updateSubject(index, "marks_obtained", "")
                                  : updateSubject(
                                      index,
                                      "marks_obtained",
                                      e.target.value
                                    )
                              }
                              className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="0"
                              required
                            />
                          </td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              step="0.01"
                              value={subject.total_marks}
                              onChange={(e) =>
                                // allow backspace and clear
                                e.key === "Backspace" || e.key === "Delete"
                                  ? updateSubject(index, "total_marks", "")
                                  : updateSubject(
                                      index,
                                      "total_marks",
                                      e.target.value
                                    )
                              }
                              className="w-full px-2 py-1 border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="100"
                              required
                            />
                          </td>
                          <td className="px-4 py-2 text-center font-medium">
                            {percentage}%
                          </td>
                          <td className="px-4 py-2 text-center">
                            {resultData.subjects.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeSubjectRow(index)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Total Row */}
                    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
                      <td className="px-4 py-2">TOTAL</td>
                      <td className="px-4 py-2">{calculateTotalMarks()}</td>
                      <td className="px-4 py-2">{calculateTotalMaxMarks()}</td>
                      <td className="px-4 py-2 text-center text-lg text-blue-600">
                        {calculateOverallPercentage()}%
                      </td>
                      <td className="px-4 py-2"></td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <button
                type="button"
                onClick={addSubjectRow}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors flex items-center justify-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Subject
              </button>

              <div className="space-y-2">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary text-white"
                >
                  {loading ? "Saving..." : "Save Results"}
                </Button>
                {recordsAddedCount.results > 0 && (
                  <button
                    type="button"
                    onClick={() =>
                      (window.location.href = `/admin/students/${student.id}`)
                    }
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Close & View Profile →
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default StudentUpdateModal;
